import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Plane, PlaneTakeoff, PlaneLanding, MapPin, Lightbulb, Car, DoorOpen,
  TrainFront, ArrowRight, Briefcase, Sparkles, Search,
} from "lucide-react";
import api from "../lib/api";
import IAPromoStrip from "../components/india/IAPromoStrip";

/* IndianAirports — My Flight: a personalized travel-day airport page.
   Airline detected from flight-number prefix; origin/destination airport intel
   composed into one page, with TravelerPulse save/plan funnels. */

export default function IndiaMyFlight() {
  const [params, setParams] = useSearchParams();
  const [airports, setAirports] = useState([]);
  const [flight, setFlight] = useState(params.get("flight") || "");
  const [origin, setOrigin] = useState(params.get("from") || "");
  const [dest, setDest] = useState(params.get("to") || "");
  const [date, setDate] = useState(params.get("date") || "");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "My Flight — IndianAirports.com";
    api.get("/india/airports").then((r) => setAirports(r.data.items || []));
    if (params.get("flight") && params.get("from") && params.get("to")) lookup(params.get("flight"), params.get("from"), params.get("to"));
    window.scrollTo(0, 0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const lookup = async (f = flight, o = origin, d = dest) => {
    setErr(""); setLoading(true); setResult(null);
    try {
      const { data } = await api.get("/india/flight/lookup", { params: { flight: f, origin: o, dest: d } });
      setResult(data);
      setParams({ flight: f, from: o, to: d, ...(date ? { date } : {}) }, { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not find that flight — check the details.");
    } finally { setLoading(false); }
  };

  const canSearch = flight.trim().length >= 3 && origin && dest && origin !== dest;
  const hubLink = result
    ? `/travel-hub?ref=indianairports&src=%2Findia%2Fmy-flight&flight=${encodeURIComponent(result.flight.code)}&from=${result.origin.iata}&to=${result.destination.iata}${date ? `&date=${date}` : ""}`
    : "/travel-hub?ref=indianairports&src=%2Findia%2Fmy-flight";

  return (
    <div data-testid="india-myflight-page">
      <div className="bg-gradient-to-br from-sky-50 via-white to-orange-50 border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-5 py-10">
          <p className="text-xs font-800 uppercase tracking-wide text-blue-700 inline-flex items-center gap-1.5"><Plane className="w-3.5 h-3.5" /> Travel day</p>
          <h1 className="mt-1 font-heading font-900 text-4xl sm:text-5xl tracking-tight" data-testid="india-myflight-title">My Flight</h1>
          <p className="mt-2 text-base text-zinc-600 max-w-2xl">Enter your flight and we'll build your personal airport day — terminals, tips and transport for departure and arrival.</p>

          {/* Search form */}
          <div className="mt-5 bg-white border border-zinc-200 rounded-2xl p-4 grid sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="india-myflight-form">
            <label className="block">
              <span className="text-[10px] font-800 uppercase text-zinc-400">Flight number</span>
              <input value={flight} onChange={(e) => setFlight(e.target.value)} placeholder="e.g. 6E 123"
                data-testid="india-myflight-number"
                className="mt-1 w-full text-sm font-700 border border-zinc-300 rounded-xl px-3 py-2 focus:outline-none focus:border-blue-400" />
            </label>
            <label className="block">
              <span className="text-[10px] font-800 uppercase text-zinc-400">From</span>
              <select value={origin} onChange={(e) => setOrigin(e.target.value)} data-testid="india-myflight-origin"
                className="mt-1 w-full text-sm border border-zinc-300 rounded-xl px-2.5 py-2 bg-white focus:outline-none focus:border-blue-400">
                <option value="">Departure airport…</option>
                {airports.map((a) => <option key={a.iata} value={a.iata}>{a.city} — {a.iata}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] font-800 uppercase text-zinc-400">To</span>
              <select value={dest} onChange={(e) => setDest(e.target.value)} data-testid="india-myflight-dest"
                className="mt-1 w-full text-sm border border-zinc-300 rounded-xl px-2.5 py-2 bg-white focus:outline-none focus:border-blue-400">
                <option value="">Arrival airport…</option>
                {airports.map((a) => <option key={a.iata} value={a.iata}>{a.city} — {a.iata}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] font-800 uppercase text-zinc-400">Travel date (optional)</span>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="india-myflight-date"
                className="mt-1 w-full text-sm border border-zinc-300 rounded-xl px-3 py-2 bg-white focus:outline-none focus:border-blue-400" />
            </label>
            <div className="flex items-end">
              <button onClick={() => lookup()} disabled={!canSearch || loading} data-testid="india-myflight-search"
                className="w-full inline-flex items-center justify-center gap-1.5 bg-blue-700 text-white text-sm font-800 rounded-xl px-4 py-2.5 hover:bg-blue-800 disabled:opacity-40">
                <Search className="w-4 h-4" /> {loading ? "Building…" : "Build my day"}
              </button>
            </div>
          </div>
          {err && <p className="mt-2 text-sm font-700 text-red-600" data-testid="india-myflight-error">{err}</p>}
        </div>
      </div>

      {result && (
        <div className="max-w-5xl mx-auto px-5 py-8 space-y-8" data-testid="india-myflight-result">
          {/* Flight strip */}
          <div className="bg-zinc-900 text-white rounded-2xl p-5 flex flex-wrap items-center gap-x-6 gap-y-3" data-testid="india-myflight-strip">
            <div>
              <div className="text-[10px] font-800 uppercase text-zinc-400">Flight</div>
              <div className="font-heading font-900 text-2xl">{result.flight.code}</div>
              {result.airline && (
                result.airline.in_directory
                  ? <Link to={`/india/airlines/${result.airline.slug}`} className="text-xs font-700 text-sky-400 hover:underline" data-testid="india-myflight-airline">{result.airline.name} →</Link>
                  : <span className="text-xs font-700 text-zinc-300" data-testid="india-myflight-airline">{result.airline.name}</span>
              )}
            </div>
            <div className="flex items-center gap-3 font-heading font-900 text-xl">
              <span>{result.origin.iata}</span>
              <ArrowRight className="w-5 h-5 text-orange-400" />
              <span>{result.destination.iata}</span>
            </div>
            {date && <div className="text-sm text-zinc-300">{date}</div>}
            <Link to={hubLink} data-testid="india-myflight-save-hub"
              className="ml-auto inline-flex items-center gap-1.5 bg-white text-zinc-900 text-xs font-800 rounded-full px-4 py-2 hover:bg-sky-100">
              <Briefcase className="w-3.5 h-3.5" /> Save to My Travel Hub
            </Link>
          </div>

          <AirportDay a={result.origin} kind="departure" />
          <AirportDay a={result.destination} kind="arrival" />

          {/* Route + planning funnels */}
          <div className="flex flex-wrap gap-3">
            <Link to={`/india/airports/routes/${result.route_pair}`} data-testid="india-myflight-route-link"
              className="inline-flex items-center gap-1.5 text-sm font-800 text-blue-700 border border-blue-300 rounded-full px-4 py-2 hover:bg-blue-50">
              <Plane className="w-4 h-4" /> Full {result.route_pair} route guide
            </Link>
            <Link to={`/plan-ai?ref=indianairports&src=%2Findia%2Fmy-flight&dest=${encodeURIComponent(result.destination.city)}`} data-testid="india-myflight-planai"
              className="inline-flex items-center gap-1.5 text-sm font-800 text-violet-700 border border-violet-300 rounded-full px-4 py-2 hover:bg-violet-50">
              <Sparkles className="w-4 h-4" /> Plan my {result.destination.city} days with AI
            </Link>
          </div>

          <IAPromoStrip city={result.destination.city} />
        </div>
      )}
    </div>
  );
}

function AirportDay({ a, kind }) {
  const dep = kind === "departure";
  const Icon = dep ? PlaneTakeoff : PlaneLanding;
  return (
    <section className="bg-white border border-zinc-200 rounded-2xl overflow-hidden" data-testid={`india-myflight-${kind}`}>
      <div className="relative h-28">
        <img src={a.image_url} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/80 to-transparent" />
        <div className="absolute bottom-2.5 left-4 right-4 text-white flex flex-wrap items-baseline gap-x-3">
          <span className="text-[10px] font-800 uppercase tracking-wide bg-white/20 rounded px-1.5 py-0.5 inline-flex items-center gap-1"><Icon className="w-3 h-3" /> {dep ? "Departure" : "Arrival"}</span>
          <Link to={`/india/airports/${a.slug}`} className="font-heading font-900 text-lg hover:underline">{a.name}</Link>
          <span className="text-xs opacity-90 inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {a.city}, {a.state} · <b className="font-mono">{a.iata}</b></span>
        </div>
      </div>
      <div className="p-4 sm:p-5 grid md:grid-cols-2 gap-5">
        <div>
          {a.terminals?.length > 0 && (
            <>
              <div className="text-xs font-800 uppercase text-zinc-400 flex items-center gap-1.5"><DoorOpen className="w-3.5 h-3.5 text-blue-600" /> Terminals</div>
              <div className="mt-1.5 space-y-1.5">
                {a.terminals.map((t) => (
                  <div key={t.name} className="text-xs bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2">
                    <b className="text-zinc-800">{t.name}</b>{t.focus ? <span className="text-zinc-500"> — {t.focus}</span> : null}
                  </div>
                ))}
              </div>
            </>
          )}
          {a.tips?.length > 0 && (
            <p className="mt-3 text-xs text-zinc-600 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 flex items-start gap-1.5">
              <Lightbulb className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
              <span><b>{dep ? "Before you fly" : "On arrival"}:</b> {a.tips[dep ? 0 : 1] || a.tips[0]}</span>
            </p>
          )}
        </div>
        <div>
          <div className="text-xs font-800 uppercase text-zinc-400 flex items-center gap-1.5"><Car className="w-3.5 h-3.5 text-blue-600" /> {dep ? "Getting to the airport" : "Getting to the city"}</div>
          <div className="mt-1.5 space-y-1">
            {(a.ground_transport || []).slice(0, 4).map((g) => (
              <div key={g.mode} className="text-xs flex items-baseline gap-2">
                <b className="text-zinc-700 shrink-0 inline-flex items-center gap-1">{g.mode === "Metro" && <TrainFront className="w-3 h-3 text-emerald-600" />}{g.mode}</b>
                <span className="text-zinc-500 flex-1">{g.time || ""}{g.fare && !/^(n\/a|varies)$/i.test(g.fare.trim()) ? ` · ${g.fare}` : ""}</span>
              </div>
            ))}
            {a.distance_to_city && <div className="text-[11px] text-zinc-400 mt-1">City center: {a.distance_to_city}</div>}
          </div>
        </div>
      </div>
    </section>
  );
}
